import React, { useEffect, useState } from 'react';
import '../../styles/admin/ProgramContent.css';
import programService from '../../services/programService';
import formService from '../../services/formService';
import ImageGallery from '../../components/ImageGallery';
import StorageVideoUpload from '../../components/admin/StorageVideoUpload';
import { buildProgramPathSegments } from '../../utils/programStoragePaths';
import { ImageIcon, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const emptyContent = {
  hero_title: '',
  intro_title: '',
  intro_body: '',
  warmup_title: '',
  warmup_video_url: '',
  warmup_storage_file_uuid: '',
  phases: [],
  upsell_type: 'program', // 'program' or 'custom'
  upsell_program_id: '',
  upsell_title: '',
  upsell_text: '',
  upsell_image: '',
  upsell_secondary_enabled: false,
  upsell_secondary_text: 'Book a Call',
  upsell_secondary_link: '',
  upsell_secondary_bg_color: '#0f766e',
  upsell_secondary_text_color: '#ffffff'
};

export default function ProgramContentAdmin() {
  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [content, setContent] = useState(emptyContent);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    loadPrograms();
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      setFormsLoading(true);
      const res = await formService.getFormsAdmin();
      if (res.success) setForms(res.forms || []);
    } catch (err) {
      console.error('Failed to load forms', err);
    } finally {
      setFormsLoading(false);
    }
  };

  const loadPrograms = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/programs/admin/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPrograms(data);
      }
    } catch (err) {
      setError('Failed to load programs');
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async (programId) => {
    if (!programId) return;
    setLoading(true);
    setMessage('');
    setError('');
    try {
      const data = await programService.getProgramContentAdmin(programId);
      setContent({
        hero_title: data.hero_title || '',
        intro_title: data.intro_title || '',
        intro_body: data.intro_body || '',
        warmup_title: data.warmup_title || '',
        warmup_video_url: data.warmup_video_url || '',
        warmup_storage_file_uuid: data.warmup_storage_file_uuid || '',
        phases: Array.isArray(data.phases) ? data.phases : [],
        upsell_type: data.upsell_type || 'program',
        upsell_program_id: data.upsell_program_id || '',
        upsell_title: data.upsell_title || '',
        upsell_text: data.upsell_text || '',
        upsell_image: data.upsell_image || '',
        upsell_secondary_enabled: data.upsell_secondary_enabled === true || data.upsell_secondary_enabled === 1 || data.upsell_secondary_enabled === '1',
        upsell_secondary_text: data.upsell_secondary_text || 'Book a Call',
        upsell_secondary_link: data.upsell_secondary_link || '',
        upsell_secondary_bg_color: data.upsell_secondary_bg_color || '#0f766e',
        upsell_secondary_text_color: data.upsell_secondary_text_color || '#ffffff'
      });
    } catch (err) {
      setError('Could not load program content');
      setContent(emptyContent);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProgram = (programId) => {
    setSelectedProgramId(programId);
    setContent(emptyContent);
    if (programId) {
      loadContent(programId);
    }
  };

  const updateField = (key, value) => {
    setContent((prev) => ({ ...prev, [key]: value }));
  };

  const addPhase = () => {
    setContent((prev) => ({
      ...prev,
      phases: [...prev.phases, { title: '', goal: '', days: [] }]
    }));
  };

  const updatePhase = (index, updates) => {
    setContent((prev) => {
      const phases = [...prev.phases];
      phases[index] = { ...phases[index], ...updates };
      return { ...prev, phases };
    });
  };

  const removePhase = (index) => {
    setContent((prev) => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }));
  };

  const addDay = (phaseIndex) => {
    setContent((prev) => {
      const phases = [...prev.phases];
      const days = phases[phaseIndex]?.days || [];
      phases[phaseIndex] = {
        ...phases[phaseIndex],
        days: [
          ...days,
          {
            day_number: days.length + 1,
            title: '',
            focus: '',
            action_step: '',
            video_url: '',
            storage_file_uuid: '',
            is_bonus: 0,
            immediate_unlock: 0,
            exception_unlock: 0,
            join_community: 0,
            join_community_link: '',
            require_offer_id: '',
            form_id: null
          }
        ]
      };
      return { ...prev, phases };
    });
  };

  const updateDay = (phaseIndex, dayIndex, updates) => {
    setContent((prev) => {
      const phases = [...prev.phases];
      const days = [...(phases[phaseIndex]?.days || [])];
      days[dayIndex] = { ...days[dayIndex], ...updates };
      phases[phaseIndex] = { ...phases[phaseIndex], days };
      return { ...prev, phases };
    });
  };

  const removeDay = (phaseIndex, dayIndex) => {
    setContent((prev) => {
      const phases = [...prev.phases];
      const days = phases[phaseIndex]?.days.filter((_, i) => i !== dayIndex) || [];
      phases[phaseIndex] = { ...phases[phaseIndex], days };
      return { ...prev, phases };
    });
  };

  const selectedProgram = programs.find((p) => p.program_id === selectedProgramId);
  const selectedProgramName = selectedProgram?.name || selectedProgramId;

  const handleSave = async () => {
    if (!selectedProgramId) {
      setError('Select a program first');
      return;
    }
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await programService.saveProgramContent(selectedProgramId, content);
      setMessage('Program content saved');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProgramId) return;
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      setConfirmError('Please type DELETE to confirm.');
      return;
    }
    setConfirmError('');
    setConfirmOpen(false);
    setConfirmText('');
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await programService.deleteProgramContent(selectedProgramId);
      setContent(emptyContent);
      setMessage('Program content deleted');
    } catch (err) {
      setError('Failed to delete content');
    } finally {
      setSaving(false);
    }
  };

  const openGallery = () => {
    setGalleryOpen(true);
  };

  const handleSelectImage = (url) => {
    updateField('upsell_image', url);
  };

  const clearImage = () => {
    updateField('upsell_image', '');
  };

  // Helper to get full image URL
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  return (
    <div className="admin-program-content">
      <div className="pc-header">
        <div>
          <h1>Program Page Content</h1>
          <p>Manage daily workouts, videos, and copy for each program page.</p>
        </div>
        <div className="pc-actions">
          <button className="btn-secondary" onClick={() => selectedProgramId && loadContent(selectedProgramId)} disabled={!selectedProgramId || loading}>
            Refresh
          </button>
          <button className="btn-danger" onClick={() => { setConfirmOpen(true); setConfirmText(''); setConfirmError(''); }} disabled={!selectedProgramId || saving}>
            Delete Content
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={!selectedProgramId || saving}>
            {saving ? 'Saving...' : 'Save Content'}
          </button>
        </div>
      </div>

      <div className="pc-card">
        <div className="pc-row">
          <label>Choose Program</label>
          <select value={selectedProgramId} onChange={(e) => handleSelectProgram(e.target.value)}>
            <option value="">Select a program</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>
                {p.program_id} — {p.name}
              </option>
            ))}
          </select>
        </div>

        {loading && (
          <div className="pc-hint">Loading...</div>
        )}
        {message && <div className="pc-message success">{message}</div>}
        {error && <div className="pc-message error">{error}</div>}

        {selectedProgramId && (
          <>
            <div className="pc-grid">
              <div className="pc-row">
                <label>Hero Title</label>
                <input value={content.hero_title} onChange={(e) => updateField('hero_title', e.target.value)} placeholder="14 Days Workouts" />
              </div>
              <div className="pc-row">
                <label>Intro Title</label>
                <input value={content.intro_title} onChange={(e) => updateField('intro_title', e.target.value)} placeholder="The Feel-Good Challenge" />
              </div>
              <div className="pc-row">
                <label>Intro Body</label>
                <textarea rows="3" value={content.intro_body} onChange={(e) => updateField('intro_body', e.target.value)} placeholder="Welcome text shown at the top of the page" />
              </div>
              <div className="pc-row">
                <label>Warmup Title</label>
                <input value={content.warmup_title} onChange={(e) => updateField('warmup_title', e.target.value)} placeholder="Warm Up" />
              </div>
            <div className="pc-row">
              <label>Warmup Video URL (embed or YouTube link)</label>
              <input value={content.warmup_video_url} onChange={(e) => updateField('warmup_video_url', e.target.value)} placeholder="https://www.youtube.com/embed/..." />
            </div>
            <div className="pc-row">
              <label>Warmup Backblaze video</label>
              <StorageVideoUpload
                value={content.warmup_storage_file_uuid || ''}
                onChange={(uuid) => updateField('warmup_storage_file_uuid', uuid)}
                disabled={!selectedProgramId || loading || saving}
                isPrivate
                label="Upload"
                pathSegments={buildProgramPathSegments({
                  programName: selectedProgramName,
                  programId: selectedProgramId,
                  section: 'Warmup',
                })}
              />
            </div>
          </div>

          <div className="pc-section-header" style={{ marginTop: 24 }}>
            <h2>Upsell (shown after final video)</h2>
          </div>
          
          <div className="pc-upsell-container">
            <div className="pc-row">
              <label>Upsell Type</label>
              <select 
                value={content.upsell_type} 
                onChange={(e) => updateField('upsell_type', e.target.value)}
                className="pc-upsell-type-select"
              >
                <option value="program">Direct Program Link</option>
                <option value="custom">Custom Content</option>
              </select>
            </div>

            <div className="pc-upsell-content">
              {content.upsell_type === 'program' ? (
                // Direct Program Selection
                <div className="pc-upsell-mode pc-upsell-direct">
                  <div className="pc-row">
                    <label>Select Program to Link</label>
                    <select 
                      value={content.upsell_program_id} 
                      onChange={(e) => {
                        const selectedProgram = programs.find(p => p.program_id === e.target.value);
                        updateField('upsell_program_id', e.target.value);
                        if (selectedProgram) {
                          // Auto-populate with program details
                          updateField('upsell_title', `Continue with ${selectedProgram.name}`);
                          updateField('upsell_text', selectedProgram.description || 'Ready for the next challenge?');
                          updateField('upsell_image', selectedProgram.image || '');
                        }
                      }}
                    >
                      <option value="">Select a program</option>
                      {programs.filter(p => p.program_id !== selectedProgramId).map((p) => (
                        <option key={p.program_id} value={p.program_id}>
                          {p.program_id} — {p.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                // Custom Content Fields
                <div className="pc-upsell-mode pc-upsell-custom">
                  <div className="pc-grid">
                    <div className="pc-row">
                      <label>Target Program ID (where button should navigate)</label>
                      <select 
                        value={content.upsell_program_id} 
                        onChange={(e) => updateField('upsell_program_id', e.target.value)}
                      >
                        <option value="">Select target program</option>
                        {programs.filter(p => p.program_id !== selectedProgramId).map((p) => (
                          <option key={p.program_id} value={p.program_id}>
                            {p.program_id} — {p.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="pc-row">
                      <label>Custom Title</label>
                      <input 
                        value={content.upsell_title} 
                        onChange={(e) => updateField('upsell_title', e.target.value)} 
                        placeholder="Keep going with..." 
                      />
                    </div>
                  </div>
                  <div className="pc-row">
                    <label>Custom Text</label>
                    <textarea 
                      rows="2" 
                      value={content.upsell_text} 
                      onChange={(e) => updateField('upsell_text', e.target.value)} 
                      placeholder="Short pitch for the next program" 
                    />
                  </div>
                  <div className="pc-row">
                    <label>Custom Image</label>
                    {content.upsell_image ? (
                      <div className="pc-image-preview" onClick={openGallery}>
                        <img 
                          src={getImageUrl(content.upsell_image)}
                          alt="Upsell preview"
                          crossOrigin="anonymous"
                        />
                        <button 
                          type="button" 
                          className="pc-image-remove"
                          onClick={(e) => { e.stopPropagation(); clearImage(); }}
                        >
                          <X size={14} />
                        </button>
                        <div className="pc-image-overlay">
                          Click to change
                        </div>
                      </div>
                    ) : (
                      <div className="pc-image-placeholder" onClick={openGallery}>
                        <ImageIcon size={24} />
                        <span>Select Image</span>
                        <small>Click to browse</small>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="pc-upsell-mode pc-upsell-custom">
                <div className="pc-row">
                  <label className="pc-checkbox">
                    <input
                      type="checkbox"
                      checked={!!content.upsell_secondary_enabled}
                      onChange={(e) => updateField('upsell_secondary_enabled', e.target.checked)}
                    />
                    Enable "Book a Call" Button
                  </label>
                </div>
                {content.upsell_secondary_enabled && (
                  <div className="pc-grid pc-two-col">
                    <div className="pc-row">
                      <label>Button Text</label>
                      <input
                        value={content.upsell_secondary_text}
                        onChange={(e) => updateField('upsell_secondary_text', e.target.value)}
                        placeholder="Book a Call"
                      />
                    </div>
                    <div className="pc-row">
                      <label>Button Link</label>
                      <input
                        value={content.upsell_secondary_link}
                        onChange={(e) => updateField('upsell_secondary_link', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                    <div className="pc-row">
                      <label>Button Background Color</label>
                      <input
                        type="color"
                        value={content.upsell_secondary_bg_color}
                        onChange={(e) => updateField('upsell_secondary_bg_color', e.target.value)}
                      />
                    </div>
                    <div className="pc-row">
                      <label>Button Text Color</label>
                      <input
                        type="color"
                        value={content.upsell_secondary_text_color}
                        onChange={(e) => updateField('upsell_secondary_text_color', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Preview Section */}
              {(content.upsell_program_id || (content.upsell_secondary_enabled && content.upsell_secondary_link)) && (content.upsell_title || content.upsell_text || content.upsell_image || content.upsell_secondary_enabled) && (
                <div className="pc-upsell-preview">
                  <label>Preview</label>
                  <div className="pc-preview-card">
                    {content.upsell_image && (
                      <img 
                        src={getImageUrl(content.upsell_image)}
                        alt="Preview"
                        className="pc-preview-image"
                      />
                    )}
                    {content.upsell_title && (
                      <h4 className="pc-preview-title">
                        {content.upsell_title}
                      </h4>
                    )}
                    {content.upsell_text && (
                      <p className="pc-preview-text">
                        {content.upsell_text}
                      </p>
                    )}
                    {content.upsell_program_id && (
                      <div className="pc-preview-button">
                        Start Program -> {content.upsell_program_id}
                      </div>
                    )}
                    {content.upsell_secondary_enabled && (
                      <div
                        className="pc-preview-button pc-preview-button-secondary"
                        style={{
                          background: content.upsell_secondary_bg_color || '#0f766e',
                          color: content.upsell_secondary_text_color || '#ffffff'
                        }}
                      >
                        {content.upsell_secondary_text || 'Book a Call'}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="pc-section-header">
            <h2>Phases</h2>
            <button className="btn-secondary" onClick={addPhase}>Add Phase</button>
          </div>

            {content.phases.length === 0 && (
              <div className="pc-hint">No phases yet. Add one to start.</div>
            )}

            {content.phases.map((phase, phaseIndex) => (
              <div key={phaseIndex} className="pc-phase">
                <div className="pc-phase-header">
                  <div>
                    <input
                      value={phase.title}
                      onChange={(e) => updatePhase(phaseIndex, { title: e.target.value })}
                      placeholder={`Phase ${phaseIndex + 1} title`}
                    />
                    <input
                      value={phase.goal || ''}
                      onChange={(e) => updatePhase(phaseIndex, { goal: e.target.value })}
                      placeholder="Goal / subtitle"
                    />
                  </div>
                  <div className="pc-phase-actions">
                    <button className="btn-secondary" onClick={() => addDay(phaseIndex)}>Add Day</button>
                    <button className="btn-link danger" onClick={() => removePhase(phaseIndex)}>Remove Phase</button>
                  </div>
                </div>

                {(phase.days || []).length === 0 && <div className="pc-hint">No days yet.</div>}

                {(phase.days || []).map((day, dayIndex) => (
                  <div key={dayIndex} className="pc-day">
                    <div className="pc-day-header">
                      <div className="pc-day-meta">
                        <label>Day #</label>
                        <input
                          type="number"
                          value={day.day_number || ''}
                          onChange={(e) => updateDay(phaseIndex, dayIndex, { day_number: Number(e.target.value) })}
                        />
                        <label className="pc-checkbox">
                          <input
                            type="checkbox"
                            checked={!!day.is_bonus}
                            onChange={(e) => updateDay(phaseIndex, dayIndex, { is_bonus: e.target.checked ? 1 : 0 })}
                          />
                          Bonus
                        </label>
                      </div>
                      <button className="btn-link danger" onClick={() => removeDay(phaseIndex, dayIndex)}>Remove</button>
                    </div>
                    <div className="pc-grid">
                      <div className="pc-row">
                        <label>Title</label>
                        <input value={day.title || ''} onChange={(e) => updateDay(phaseIndex, dayIndex, { title: e.target.value })} />
                      </div>
                      <div className="pc-row">
                        <label>Focus</label>
                        <input value={day.focus || ''} onChange={(e) => updateDay(phaseIndex, dayIndex, { focus: e.target.value })} />
                      </div>
                      <div className="pc-row">
                        <label>Tiny Action Step</label>
                        <input value={day.action_step || ''} onChange={(e) => updateDay(phaseIndex, dayIndex, { action_step: e.target.value })} />
                      </div>
                      <div className="pc-row">
                        <label>Video URL (embed or YouTube link)</label>
                        <input value={day.video_url || ''} onChange={(e) => updateDay(phaseIndex, dayIndex, { video_url: e.target.value })} />
                      </div>
                      <div className="pc-row">
                        <label>Backblaze video</label>
                        <StorageVideoUpload
                          value={day.storage_file_uuid || ''}
                          onChange={(uuid) => updateDay(phaseIndex, dayIndex, { storage_file_uuid: uuid })}
                          disabled={!selectedProgramId || loading || saving}
                          isPrivate
                          label="Upload"
                          pathSegments={buildProgramPathSegments({
                            programName: selectedProgramName,
                            programId: selectedProgramId,
                            section: 'Workouts',
                            phaseNumber: phaseIndex + 1,
                            dayNumber: day.day_number || dayIndex + 1,
                          })}
                        />
                      </div>
                      <div className="pc-day-options">
                        <div className="pc-day-option-toggles">
                          <label className="pc-checkbox">
                            <input
                              type="checkbox"
                              checked={!!day.immediate_unlock}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { immediate_unlock: e.target.checked ? 1 : 0 })}
                            />
                            Unlock immediately (exception)
                          </label>
                          <label className="pc-checkbox">
                            <input
                              type="checkbox"
                              checked={!!day.exception_unlock}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { exception_unlock: e.target.checked ? 1 : 0 })}
                            />
                            Unlock one day early
                          </label>
                          <label className="pc-checkbox">
                            <input
                              type="checkbox"
                              checked={!!day.join_community}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { join_community: e.target.checked ? 1 : 0 })}
                            />
                            Show "Join Community" button
                          </label>
                        </div>

                        <div className="pc-day-option-fields">
                          <div className="pc-row">
                            <label>Require offer id (optional)</label>
                            <input
                              value={day.require_offer_id || ''}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { require_offer_id: e.target.value })}
                              placeholder="e.g., 4w or 6w"
                            />
                            <small>Only unlocks for purchases matching this offer id</small>
                          </div>
                          <div className="pc-row">
                            <label>Join Community URL (optional)</label>
                            <input
                              value={day.join_community_link || ''}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { join_community_link: e.target.value })}
                              placeholder="https://chat.whatsapp.com/..."
                              disabled={!day.join_community}
                            />
                            <small>If empty, program-level community link is used.</small>
                          </div>
                          <div className="pc-row">
                            <label>Attach Form (after clicking Next)</label>
                            <select
                              value={day.form_id || ''}
                              onChange={(e) => updateDay(phaseIndex, dayIndex, { form_id: e.target.value ? Number(e.target.value) : null })}
                            >
                              <option value="">No form</option>
                              {formsLoading && <option>Loading...</option>}
                              {!formsLoading && forms.map((f) => (
                                <option key={f.id} value={f.id}>{f.title}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </>
        )}
      </div>

      {confirmOpen && (
        <div className="pc-modal-backdrop">
          <div className="pc-modal">
            <h3>Confirm Delete</h3>
            <p>This will remove the saved content for <strong>{selectedProgramId}</strong>. Type <code>DELETE</code> to proceed.</p>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
            />
            {confirmError && <div className="pc-message error" style={{ marginTop: 8 }}>{confirmError}</div>}
            <div className="pc-modal-actions">
              <button className="btn-secondary" onClick={() => { setConfirmOpen(false); setConfirmText(''); setConfirmError(''); }}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <ImageGallery 
        isOpen={galleryOpen} 
        onClose={() => setGalleryOpen(false)}
        onSelect={handleSelectImage}
        currentImage={content.upsell_image}
      />
    </div>
  );
}



